import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"
import { detachGhostOnStart } from "../helpers/sortable_ghost"

// The server refused an arrangement and said why (CustomReportTypes::ArrangementsController).
class ArrangementRefused extends Error {}

export default class extends Controller {
  static targets = [
    "canvas",
    "placeholder",
    "dragHandle",
    "fieldPalette",
    "insertMenu",
    "groupContainer",
  ]

  static values = {
    reportTypeId: Number,
    saveFirstMessage: {
      type: String,
      default: "Please save the report template first before adding fields.",
    },
    createErrorMessage: { type: String, default: "Could not create field/block." },
    fieldCreateErrorMessage: { type: String, default: "Could not create field." },
    blockCreateErrorMessage: { type: String, default: "Could not create block." },
    arrangeErrorMessage: { type: String, default: "Could not save the new order." },
    // Translated field- and block-type labels, keyed by type — see
    // CustomReportTypesHelper#report_builder_default_labels.
    defaultLabels: Object,
  }

  // The label a newly inserted field or block starts out with. Falls back to a capitalised
  // type name so an unmapped type still creates something the admin can rename.
  defaultLabelFor(type) {
    return this.defaultLabelsValue[type] || type.charAt(0).toUpperCase() + type.slice(1)
  }

  connect() {
    this.initializeSortable()
    this.initializeGroupSortables()
    // Close menu when clicking outside
    document.addEventListener("click", this.handleOutsideClick.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick.bind(this))
  }

  handleOutsideClick(event) {
    if (
      this.hasInsertMenuTarget &&
      !this.insertMenuTarget.contains(event.target) &&
      !event.target.closest('[data-action*="toggleInsertMenu"]')
    ) {
      this.insertMenuTarget.classList.add("hidden")
    }
  }

  // One Sortable per container — the canvas, every group and every Abschnitt — all in one
  // shared group, so an element can be dragged from any container into any other that may
  // hold it (#canAccept). The server writes parents and positions of every touched container
  // in one go (CustomReports::BuilderArrangement).
  initializeSortable() {
    if (this.hasCanvasTarget) {
      new Sortable(this.canvasTarget, this.sortableOptions())
    }
  }

  initializeGroupSortables() {
    this.groupContainerTargets.forEach((container) => {
      this.initializeSortableOnGroup(container)
    })
  }

  // Called automatically when a new groupContainer target is connected (e.g., via Turbo)
  groupContainerTargetConnected(container) {
    this.initializeSortableOnGroup(container)
  }

  initializeSortableOnGroup(container) {
    if (container.dataset.sortableInitialized) return

    new Sortable(container, this.sortableOptions())
    container.dataset.sortableInitialized = "true"
  }

  sortableOptions() {
    return {
      group: {
        name: "report-builder",
        put: (to, _from, dragEl) => this.canAccept(to.el, dragEl),
      },
      animation: 150,
      handle: '[data-report-builder-target="dragHandle"]',
      // Only cards are items, so an empty container's hint text does not count as one
      draggable: "[data-field-id], [data-block-id]",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      forceFallback: true,
      fallbackClass: "sortable-fallback",
      // Nested containers: let the pointer reach the inner ones and drop into empty ones
      fallbackOnBody: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 16,
      onStart: detachGhostOnStart,
      onEnd: (evt) => this.handleMove(evt),
    }
  }

  // An Abschnitt holds fields only, and no other Abschnitt; a group holds anything except
  // itself. The server checks the same rules.
  canAccept(container, item) {
    if (item.contains(container)) return false
    if (container.dataset.sectionId) {
      return Boolean(item.dataset.fieldId) && item.dataset.fieldType !== "section"
    }
    return true
  }

  // A container's empty hint stays in the DOM and hides itself via CSS while any card is in
  // it (custom_report_blocks/_group_children), so a move does not have to touch it.
  handleMove(evt) {
    this.restoreElementStyles(evt.item)
    if (evt.from === evt.to && evt.oldIndex === evt.newIndex) return

    const containers = evt.from === evt.to ? [evt.to] : [evt.from, evt.to]
    this.sendArrangement(containers.map((container) => this.describeContainer(container)))
  }

  describeContainer(container) {
    const items = Array.from(container.children)
      .filter((child) => child.dataset.fieldId || child.dataset.blockId)
      .map((child) =>
        child.dataset.fieldId
          ? { kind: "field", id: child.dataset.fieldId }
          : { kind: "block", id: child.dataset.blockId },
      )

    if (container.dataset.sectionId) {
      return { kind: "section", id: container.dataset.sectionId, items }
    }
    if (container.dataset.groupId) return { kind: "group", id: container.dataset.groupId, items }
    return { kind: "root", items }
  }

  restoreElementStyles(element) {
    // Force browser to recalculate styles by triggering a reflow
    if (element) {
      // Remove any leftover sortable classes
      element.classList.remove(
        "sortable-ghost",
        "sortable-chosen",
        "sortable-drag",
        "sortable-fallback",
      )

      // Force style recalculation
      void element.offsetHeight
    }
  }

  // A refused or failed move leaves the page showing an order the server does not have,
  // so the message is followed by a reload.
  sendArrangement(containers) {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const reportTypeId = this.getReportTypeId()

    if (!reportTypeId) return

    fetch(`/settings/custom_report_types/${reportTypeId}/arrangement`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ containers }),
    })
      .then((response) => {
        if (response.ok) return
        return response
          .json()
          .catch(() => null)
          .then((payload) => {
            throw new ArrangementRefused(payload?.errors?.join(", ") || payload?.error || "")
          })
      })
      .catch((error) => {
        // Only the server's own reason is shown; a network error's text ("Failed to fetch")
        // is the browser's, in the browser's language.
        const message = error instanceof ArrangementRefused ? error.message : ""
        this.showNotification(message || this.arrangeErrorMessageValue, "error")
        window.setTimeout(() => window.location.reload(), 1500)
      })
  }

  toggleInsertMenu(event) {
    event.stopPropagation()
    if (this.hasInsertMenuTarget) {
      this.insertMenuTarget.classList.toggle("hidden")
    }
  }

  showFieldPalette(event) {
    this.toggleInsertMenu(event)
  }

  addField(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.fieldType
    this.createFieldOrBlock(type, false)
    this.insertMenuTarget.classList.add("hidden")
  }

  addBlock(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.blockType
    this.createFieldOrBlock(type, true)
    this.insertMenuTarget.classList.add("hidden")
  }

  getReportTypeId() {
    if (this.hasReportTypeIdValue) {
      return this.reportTypeIdValue
    }
    const form = this.element.querySelector("form")
    return form?.action.match(/custom_report_types\/(\d+)/)?.[1]
  }

  createFieldOrBlock(type, isBlock) {
    const reportTypeId = this.getReportTypeId()

    if (!reportTypeId) {
      this.showNotification(this.saveFirstMessageValue, "error")
      return
    }

    const label = this.defaultLabelFor(type)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = isBlock
      ? `/settings/custom_report_types/${reportTypeId}/custom_report_blocks`
      : `/settings/custom_report_types/${reportTypeId}/custom_report_fields`

    const body = isBlock
      ? { custom_report_block: { block_type: type, label: label } }
      : { custom_report_field: { field_type: type, label: label, placeholder: "" } }

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(body),
    })
      .then((response) => this.streamTextOrRaise(response))
      .then((html) => {
        // Process Turbo Stream response
        Turbo.renderStreamMessage(html)
        // Hide placeholder if present
        if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.add("hidden")
        }
      })
      .catch((error) => {
        this.showNotification(error.message || this.createErrorMessageValue, "error")
      })
  }

  addBlockToGroup(event) {
    event.preventDefault()
    const blockType = event.currentTarget.dataset.blockType
    const parentBlockId = event.currentTarget.dataset.parentBlockId
    this.createBlockInGroup(blockType, parentBlockId)
  }

  // "+" of a group or an Abschnitt (custom_report_fields/_container_field_menu).
  addFieldToContainer(event) {
    event.preventDefault()
    const { fieldType, parentBlockId, parentFieldId } = event.currentTarget.dataset
    event.currentTarget.closest('[data-dropdown-target="menu"]')?.classList.add("hidden")
    this.createFieldInContainer(
      fieldType,
      parentFieldId ? { parent_field_id: parentFieldId } : { parent_block_id: parentBlockId },
    )
  }

  createFieldInContainer(type, parent) {
    const reportTypeId = this.getReportTypeId()

    if (!reportTypeId) {
      this.showNotification(this.saveFirstMessageValue, "error")
      return
    }

    const label = this.defaultLabelFor(type)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/custom_report_types/${reportTypeId}/custom_report_fields`

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        custom_report_field: { field_type: type, label: label, placeholder: "", ...parent },
      }),
    })
      .then((response) => this.streamTextOrRaise(response))
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        this.showNotification(error.message || this.fieldCreateErrorMessageValue, "error")
      })
  }

  createBlockInGroup(type, parentBlockId) {
    const reportTypeId = this.getReportTypeId()

    if (!reportTypeId) {
      this.showNotification(this.saveFirstMessageValue, "error")
      return
    }

    const label = this.defaultLabelFor(type)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/custom_report_types/${reportTypeId}/custom_report_blocks`

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        custom_report_block: {
          block_type: type,
          label: label,
          parent_block_id: parentBlockId,
        },
      }),
    })
      .then((response) => this.streamTextOrRaise(response))
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        this.showNotification(error.message || this.blockCreateErrorMessageValue, "error")
      })
  }

  // Turbo-stream HTML on success. On failure, raise carrying the server's
  // validation messages when it sent any, so the caller shows the real reason
  // instead of its generic notice.
  streamTextOrRaise(response) {
    if (response.ok) return response.text()

    return response
      .json()
      .catch(() => null)
      .then((payload) => {
        throw new Error(payload?.errors?.join(", ") || "")
      })
  }

  showNotification(message, type = "info") {
    // Use existing notification system if available, otherwise fallback to alert
    if (window.Turbo && typeof window.Turbo.toast === "function") {
      window.Turbo.toast(message, type)
    } else {
      alert(message)
    }
  }
}
